import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AppStateService } from '../../services/app-state.service';
import { ActivityService } from '../../services/activity.service';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ExternalLiquidityService } from '../../services/external-liquidity.service';
import { RouterModule } from '@angular/router';
import { Web3Service } from '../../services/web3.service';
import { formatSubscript } from '../../utils/number.util';
import { getNetworkConfigById } from '../../config/network.config';
import { ethers } from 'ethers';
import { AuthService } from '../../services/auth.service';
import { SparklineChartComponent } from '../sparkline-chart/sparkline-chart.component';

@Component({
  selector: 'app-liquidity-section',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SparklineChartComponent],
  templateUrl: './liquidity-section.component.html'
})
export class LiquiditySectionComponent implements OnInit, OnDestroy {
  public state = inject(AppStateService);
  private router = inject(Router);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private externalService = inject(ExternalLiquidityService);
  private web3Service = inject(Web3Service);
  private activityService = inject(ActivityService);
  private authService = inject(AuthService);
  public activeTab = 'all';

  public kpiData = {
    tvl: { value: '$0.00', change: '0.00%' },
    volume: { value: '$0.00', change: '0.00%' },
    fees: { value: '$0.00', change: '0.00%' },
    apr: { value: '0.00%', change: '0.00%' },
    positions: 0
  };

  public yourPositions = {
    totalLiquidity: '$0.00',
    unclaimedFees: '$0.00',
    avgApr: '0.00%',
    isLoadingPositions: false,
    isLoadingUnclaimedFees: false,
    list: [] as any[]
  };

  constructor() {
    effect(() => {
      const isConnected = this.state.isWalletConnected();
      const addr = this.state.walletAddress();
      const isAuth = this.authService.isAuthenticated();
      untracked(() => {
        this.fetchYourPositions();
      });
    });

    effect(() => {
      const netId = this.state.selectedNetworkId();
      const isAuth = this.authService.isAuthenticated();
      untracked(() => {
        this.fetchPools();
        this.fetchKpiData();
        this.fetchYourPositions();
      });
    });
  }

  public pools: any[] = [];
  public allPools: any[] = [];
  public filteredPools: any[] = [];
  public loadingPools = false;

  // Search, Filter, Pagination state
  public searchQuery = '';
  public activeFeeFilter = 'All';
  public activeSort: string = 'Sort by: TVL (High to Low)';
  sortColumn: string = 'tvlUsd';
  sortDirection: string = 'desc';
  
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;

  public feeTiers = ['All', '0.01%', '0.05%', '0.30%', '1.00%'];
  public showFeeDropdown = false;
  private pollingInterval: any;

  ngOnInit() {
    this.fetchPoolsData();
    this.pollingInterval = setInterval(() => {
      this.fetchYourPositions(true);
    }, 10000);
  }

  ngOnDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  formatNumber(value: number): string {
    if (value === undefined || value === null) return '0.00';
    if (value >= 1000000000000) return (value / 1000000000000).toFixed(2) + 'T';
    if (value >= 1000000000) return (value / 1000000000).toFixed(2) + 'B';
    if (value >= 1000000) return (value / 1000000).toFixed(2) + 'M';
    if (value >= 1000) return (value / 1000).toFixed(2) + 'K';
    return value.toFixed(2);
  }

  getResolvedChainIds(): string | undefined {
    const networkId = this.state.selectedNetworkId();
    if (networkId === 'all') return undefined;
    
    const config = getNetworkConfigById(networkId);
    if (config) {
      return `${config.chainId}`;
    }
    return undefined;
  }

  async fetchPoolsData() {
    this.loadingPools = true;
    this.cdr.detectChanges();
    try {
        let feeTierParam = undefined;
        if (this.activeFeeFilter !== 'All') {
            const numStr = this.activeFeeFilter.replace('%', '');
            feeTierParam = Math.round(Number(numStr) * 10000);
        }

        const chainIds = this.getResolvedChainIds();
        const res = await this.externalService.getPools(this.currentPage, this.itemsPerPage, this.searchQuery, chainIds, this.sortColumn, this.sortDirection, feeTierParam);
        this.pools = res.data;
        this.totalItems = res.total;
    } catch (e) {
        console.error('Failed to load pools', e);
    } finally {
        this.loadingPools = false;
        this.cdr.detectChanges();
    }
  }

  onSearchChange() {
    this.currentPage = 1;
    this.fetchPoolsData();
  }

  changePage(direction: number) {
    if (direction === -1 && this.currentPage > 1) {
      this.currentPage--;
      this.fetchPoolsData();
    }
    else if (direction === 1 && this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchPoolsData();
    }
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage) || 1;
  }

  getIndex(i: number): number {
    return (Number(this.currentPage) - 1) * Number(this.itemsPerPage) + i + 1;
  }

  async fetchKpiData() {
    try {
      const chainIds = this.getResolvedChainIds();
      const overview = await this.externalService.getOverview(chainIds);
      if (overview) {
        this.kpiData.tvl.value = `$${this.formatNumber(overview.totalTvl)}`;
        this.kpiData.tvl.change = `${overview.tvlChange24h >= 0 ? '+' : ''}${overview.tvlChange24h.toFixed(2)}%`;

        this.kpiData.volume.value = `$${this.formatNumber(overview.totalVolume24h)}`;
        this.kpiData.volume.change = `${overview.volumeChange24h >= 0 ? '+' : ''}${overview.volumeChange24h.toFixed(2)}%`;

        this.kpiData.fees.value = `$${this.formatNumber(overview.totalFees24h)}`;
        this.kpiData.fees.change = `${overview.feesChange24h >= 0 ? '+' : ''}${overview.feesChange24h.toFixed(2)}%`;

        this.kpiData.apr.value = `${overview.avgApr.toFixed(2)}%`;
        this.kpiData.apr.change = `${overview.aprChange24h >= 0 ? '+' : ''}${overview.aprChange24h.toFixed(2)}%`;

        this.kpiData.positions = overview.totalPools;
        this.cdr.detectChanges();
      }
    } catch (e) {
      console.error('Failed to load KPIs', e);
    }
  }

  fetchPools() {
    this.fetchPoolsData();
  }

  fetchYourPositions(isPolling: boolean = false) {
    if (!this.state.isWalletConnected() || !this.authService.isAuthenticated()) {
      this.yourPositions = {
        totalLiquidity: '$0.00',
        unclaimedFees: '$0.00',
        avgApr: '0.00%',
        isLoadingPositions: false,
        isLoadingUnclaimedFees: false,
        list: []
      };
      this.cdr.markForCheck();
      return;
    }
    if (!isPolling) {
      this.yourPositions.isLoadingPositions = true;
      this.yourPositions.isLoadingUnclaimedFees = true;
      this.cdr.markForCheck();
    }

    const url = `${environment.apiUrl}/user/liquidity/positions-paginated?page=1&limit=5`;
    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.yourPositions.isLoadingPositions = false;
        if (res && res.status) {
          this.yourPositions.isLoadingUnclaimedFees = true;
          const positions = res.data.data;
          let computedTotalLiquidity = 0;

          this.yourPositions.list = positions.map((p: any) => {
            const pool = p.pool || {};
            const t0Sym = pool.token0Symbol || 'UNK';
            const t1Sym = pool.token1Symbol || 'UNK';
            const fee = pool.feeTier ? `${(pool.feeTier / 10000).toFixed(2)}%` : '0.00%';

            const t0 = this.state.tokens().find((t: any) => t.address.toLowerCase() === pool.token0Address?.toLowerCase());
            const t1 = this.state.tokens().find((t: any) => t.address.toLowerCase() === pool.token1Address?.toLowerCase());
            const token0UsdPrice = t0?.rate || 0.0;
            const token1UsdPrice = t1?.rate || 0.0;
            const positionLiquidityUsd = ((p.token0Amount || 0) * token0UsdPrice) + ((p.token1Amount || 0) * token1UsdPrice);

            computedTotalLiquidity += positionLiquidityUsd;

            const t0Dec = p.token0Decimals !== undefined ? p.token0Decimals : (pool.token0Decimals || 18);
            const t1Dec = p.token1Decimals !== undefined ? p.token1Decimals : (pool.token1Decimals || 18);

            const calculatePrice = (tick: number) => {
               if (tick === undefined || tick === null) return 0;
               return Math.pow(1.0001, tick) * Math.pow(10, t0Dec - t1Dec);
            };

            const minPriceRaw = calculatePrice(p.tickLower);
            const maxPriceRaw = calculatePrice(p.tickUpper);
            const formatPrice = (val: number) => {
              if (val === undefined || val === null || isNaN(val)) return '0.00';
              if (val === 0) return '0.00';
              if (val >= 1e9) return (val / 1e9).toFixed(2) + 'B';
              if (val >= 1e6) return (val / 1e6).toFixed(2) + 'M';
              if (val >= 1e3) return (val / 1e3).toFixed(2) + 'K';
              
              let plainStr = val.toFixed(8).replace(/0+$/, '');
              const match = plainStr.match(/^0\.0+/);
              if (match) {
                const zeroCount = match[0].length - 2; 
                if (zeroCount >= 5) {
                  const subscripts = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
                  const countStr = zeroCount.toString().split('').map(d => subscripts[parseInt(d)]).join('');
                  const sigDigits = plainStr.slice(match[0].length).slice(0, 4);
                  return `0.0${countStr}${sigDigits}`;
                }
              }

              return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 8
              }).format(val);
            };
            const minPrice = formatPrice(minPriceRaw);
            const maxPrice = formatPrice(maxPriceRaw);

            const currentTick = pool.currentTick !== undefined ? pool.currentTick : 0;
            const currentPriceRaw = calculatePrice(currentTick);
            const inRange = true;

            const isFullRange = (p.tickLower !== undefined && p.tickUpper !== undefined)
                ? (p.tickLower <= -887000 && p.tickUpper >= 887000)
                : false;

            return {
              id: p.tokenId,
              tokenId: p.tokenId,
              _id: p._id,
              title: `${t0Sym} / ${t1Sym}`,
              type: p.isExternal ? 'External Liquidity' : 'V3 Position',
              isStakedInFarm: !!p.isStakedInFarm,
              isExternal: !!p.isExternal,
              fee: fee,
              liquidity: `$${this.formatNumber(positionLiquidityUsd)}`,
              apr: `${(pool.apr || 0).toFixed(2)}%`,
              
              metric1Value: formatSubscript(Number(p.token0Amount) || 0),
              metric1Label: t0Sym || 'T0',
              metric2Value: formatSubscript(Number(p.token1Amount) || 0),
              metric2Label: t1Sym || 'T1',
              icon1: t0Sym || 'T0',
              icon2: t1Sym || 'T1',
              color1: (t0?.logoUrl || p.token0Image || pool?.token0Logo) ? 'transparent' : '#888',
              color2: (t1?.logoUrl || p.token1Image || pool?.token1Logo) ? 'transparent' : '#888',
              image1: t0?.logoUrl || p.token0Image || pool?.token0Logo,
              image2: t1?.logoUrl || p.token1Image || pool?.token1Logo,
              token0Amount: p.token0Amount || 0,
              token1Amount: p.token1Amount || 0,
              
              minPrice: minPrice,
              maxPrice: maxPrice,
              minPriceRaw: minPriceRaw,
              maxPriceRaw: maxPriceRaw,
              currentPriceRaw: currentPriceRaw,
              inRange: inRange,
              isFullRange: isFullRange,
              
              managerAddress: p.managerAddress || pool.managerAddress,
              platformName: p.platformName || pool.platformName,
              poolAddress: p.poolAddress || pool.address,
              token0Symbol: t0Sym,
              token1Symbol: t1Sym,
              feeToken0: p.feeToken0 || 0,
              feeToken1: p.feeToken1 || 0,
              unclaimedFeesUsd: 0,
              token0Decimals: t0Dec,
              token1Decimals: t1Dec,
              pair: `${t0Sym}-${t1Sym}`
            };
          });

          this.yourPositions.totalLiquidity = `$${this.formatNumber(computedTotalLiquidity)}`;

          this.cdr.markForCheck();
          if (positions.length > 0) {
            this.calculateLiveUnclaimedFees(positions, isPolling);
            this.fetchPositionChartData();
          } else {
            this.yourPositions.unclaimedFees = '$0.00';
            this.yourPositions.isLoadingUnclaimedFees = false;
          }
        } else {
          this.yourPositions.isLoadingUnclaimedFees = false;
        }
      },
      error: (err) => {
        this.yourPositions.isLoadingPositions = false;
        this.yourPositions.isLoadingUnclaimedFees = false;
        this.cdr.markForCheck();
        console.error('Failed to load user positions', err);
      }
    });

    const dataUrl = `${environment.apiUrl}/user/data`;
    this.http.get<any>(dataUrl).subscribe({
      next: (res) => {
        if (res && res.status && res.data) {
           this.yourPositions.avgApr = `${(res.data.averageApr || 0).toFixed(2)}%`;
           this.cdr.markForCheck();
        }
      },
      error: (err) => console.error('Failed to load user data', err)
    });
  }

  fetchPositionChartData() {
    for (const pos of this.yourPositions.list) {
      if (!pos.poolAddress) continue;
      const url = `${environment.apiUrl}/user/liquidity/pool/${pos.poolAddress}/ohlcv?timeframe=1M`;
      this.http.get<any>(url).subscribe({
        next: (res) => {
          if (res?.data?.attributes?.ohlcv_list) {
            pos.chartData = res.data.attributes.ohlcv_list.reverse();
            this.cdr.detectChanges();
          }
        },
        error: (err) => console.warn('Failed to load chart data for position', err)
      });
    }
  }

  async calculateLiveUnclaimedFees(positions: any[], isPolling: boolean = false) {
    try {
      if (!this.state.isWalletConnected()) return;
      if (!isPolling) {
        this.yourPositions.isLoadingUnclaimedFees = true;
      }
      let totalUnclaimedUsd = 0;
      
      const contracts = this.web3Service.getContractsConfig();
      for (const pos of positions) {
        let managerAddress: string | undefined = undefined;
        if (pos.isExternal) {
          if (pos.managerAddress) {
            managerAddress = pos.managerAddress;
          } else if (pos.platformName === 'Uniswap') {
            managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
          } else if (pos.platformName === 'PancakeSwap') {
            managerAddress = contracts.EXTERNAL_MANAGERS?.PancakeSwap || '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364';
          } else {
            managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
          }
        }
        
        try {
          const fees = await this.web3Service.getPendingFees(pos.tokenId, managerAddress);
          
          const dec0 = pos.token0Decimals !== undefined ? pos.token0Decimals : (pos.pool?.token0Decimals || 18);
          const dec1 = pos.token1Decimals !== undefined ? pos.token1Decimals : (pos.pool?.token1Decimals || 18);
          
          const amount0 = Number(ethers.formatUnits(fees.amount0, dec0));
          const amount1 = Number(ethers.formatUnits(fees.amount1, dec1));
          
          const t0 = this.state.tokens().find((t: any) => t.address?.toLowerCase() === pos.pool?.token0Address?.toLowerCase());
          const t1 = this.state.tokens().find((t: any) => t.address?.toLowerCase() === pos.pool?.token1Address?.toLowerCase());
          
          const token0UsdPrice = t0?.rate || 0.0;
          const token1UsdPrice = t1?.rate || 0.0;
          
          const positionUnclaimedUsd = (amount0 * token0UsdPrice) + (amount1 * token1UsdPrice);
          totalUnclaimedUsd += positionUnclaimedUsd;
          
          const uiPos = this.yourPositions.list.find((p: any) => p.tokenId === pos.tokenId);
          if (uiPos) {
            uiPos.feeToken0 = amount0;
            uiPos.feeToken1 = amount1;
            uiPos.unclaimedFeesUsd = positionUnclaimedUsd;
          }
        } catch (e) {
          console.warn(`Failed to fetch pending fees for position ${pos.tokenId}`, e);
        }
      }
      this.yourPositions.unclaimedFees = `$${formatSubscript(totalUnclaimedUsd)}`;
      this.yourPositions.isLoadingUnclaimedFees = false;
      this.cdr.detectChanges();
    } catch (error) {
      this.yourPositions.isLoadingUnclaimedFees = false;
      this.cdr.detectChanges();
      console.error('Error calculating live unclaimed fees', error);
    }
  }

  openAddLiquidityWithParams(pos: any) {
    this.router.navigate(['/liquidity/new'], {
      queryParams: {
        token1: pos.token0Symbol,
        token2: pos.token1Symbol,
        fee: pos.fee ? (Number(pos.fee.replace('%',''))).toFixed(2) + '%' : '0.30%',
        minPrice: pos.minPriceRaw,
        maxPrice: pos.maxPriceRaw,
        isFullRange: pos.isFullRange,
        amount1: pos.token0Amount,
        amount2: pos.token1Amount,
        step: 2
      }
    });
  }

  createNewPosition() {
    this.router.navigate(['/liquidity/new']);
  }

  setFeeFilter(fee: string) {
    this.activeFeeFilter = fee;
    this.showFeeDropdown = false;
    this.currentPage = 1;
    this.fetchPoolsData();
  }

  onSortChange() {
    if (this.activeSort.includes('TVL')) {
      this.sortColumn = 'tvlUsd';
      this.sortDirection = 'desc';
    } else if (this.activeSort.includes('APR')) {
      this.sortColumn = 'apr';
      this.sortDirection = 'desc';
    } else if (this.activeSort.includes('Volume')) {
      this.sortColumn = 'volume24h';
      this.sortDirection = 'desc';
    }
    this.currentPage = 1;
    this.fetchPoolsData();
  }

  goToPage(page: number | string) {
    if (typeof page === 'number') {
      this.currentPage = page;
      this.fetchPoolsData();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.fetchPoolsData();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchPoolsData();
    }
  }

  // === Claim Fees Modal Logic (Copied from Portfolio) ===
  isClaimModalOpen = false;
  isClaimingFees = false;
  selectedPosition: any = null;
  hasNoFees = false;
  formatSubscript = formatSubscript;
  isLoadingLiveFees = false;
  liveFeeAmount0 = '0.000000';
  liveFeeAmount1 = '0.000000';
  liveFeeUsdFormatted0 = '0.00';
  liveFeeUsdFormatted1 = '0.00';
  rawFeeAmount0 = 0;
  rawFeeAmount1 = 0;
  liveToken0Amount: string | null = null;
  liveToken1Amount: string | null = null;
  liveFeeUsd0 = 0;
  liveFeeUsd1 = 0;

  openClaimModal(pos: any) {
    this.selectedPosition = pos;
    this.isClaimModalOpen = true;
    this.fetchLivePositionData(pos.tokenId);
  }

  openRemoveModal(pos: any) {
    console.log('Remove modal not implemented here', pos);
  }

  openUnstakeModal(pos: any) {
    console.log('Unstake modal not implemented here', pos);
  }

  openClaimRewardModal(pos: any) {
    console.log('Claim reward modal not implemented here', pos);
  }

  closeClaimModal() {
    this.isClaimModalOpen = false;
    setTimeout(() => {
      this.selectedPosition = null;
      this.liveFeeAmount0 = '0.00';
      this.liveFeeAmount1 = '0.00';
      this.rawFeeAmount0 = 0;
      this.rawFeeAmount1 = 0;
      this.liveToken0Amount = null;
      this.liveToken1Amount = null;
    }, 300);
  }

  async fetchLivePositionData(tokenId: number) {
    try {
      this.isLoadingLiveFees = true;
      this.cdr.detectChanges();

      let managerAddress: string | undefined = undefined;
      const contracts = this.web3Service.getContractsConfig();
      if (this.selectedPosition?.isExternal) {
        if (this.selectedPosition.managerAddress) {
          managerAddress = this.selectedPosition.managerAddress;
        } else if (this.selectedPosition.platformName === 'Uniswap') {
          managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        } else if (this.selectedPosition.platformName === 'PancakeSwap') {
          managerAddress = contracts.EXTERNAL_MANAGERS?.PancakeSwap || '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364';
        } else {
          managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        }
      }

      const fees = await this.web3Service.getPendingFees(tokenId, managerAddress);
      
      const dec0 = this.selectedPosition?.token0Decimals || 18;
      const dec1 = this.selectedPosition?.token1Decimals || 18;

      const numAmount0 = Number(ethers.formatUnits(fees.amount0, dec0));
      const numAmount1 = Number(ethers.formatUnits(fees.amount1, dec1));
      this.rawFeeAmount0 = numAmount0;
      this.rawFeeAmount1 = numAmount1;

      const tokens = this.state.tokens();
      const token0Price = tokens.find((t: any) => t.symbol === this.selectedPosition?.token0Symbol)?.rate || 0;
      const token1Price = tokens.find((t: any) => t.symbol === this.selectedPosition?.token1Symbol)?.rate || 0;

      this.liveFeeUsd0 = numAmount0 * token0Price;
      this.liveFeeUsd1 = numAmount1 * token1Price;
      
      this.liveFeeUsdFormatted0 = this.formatSubscript(this.liveFeeUsd0);
      this.liveFeeUsdFormatted1 = this.formatSubscript(this.liveFeeUsd1);

      this.liveFeeAmount0 = this.formatSubscript(numAmount0);
      this.liveFeeAmount1 = this.formatSubscript(numAmount1);

      // Resync the main list's unclaimed fees with this freshly fetched live data
      const posToUpdate = this.yourPositions.list.find((p: any) => p.tokenId === tokenId);
      if (posToUpdate) {
        posToUpdate.feeToken0 = numAmount0;
        posToUpdate.feeToken1 = numAmount1;
        posToUpdate.unclaimedFeesUsd = this.liveFeeUsd0 + this.liveFeeUsd1;
        
        // Recalculate total dashboard unclaimed fees
        const newTotalUnclaimed = this.yourPositions.list.reduce((acc: number, p: any) => acc + (p.unclaimedFeesUsd || 0), 0);
        this.yourPositions.unclaimedFees = `$${formatSubscript(newTotalUnclaimed)}`;
      }

      this.checkIfNoFees();
    } catch (err) {
      console.error('Failed to fetch live position data', err);
    } finally {
      this.isLoadingLiveFees = false;
      this.cdr.detectChanges();
    }
  }

  checkIfNoFees() {
    this.hasNoFees = this.rawFeeAmount0 === 0 && this.rawFeeAmount1 === 0;
  }

  async confirmClaimFees() {
    if (!this.selectedPosition || !this.selectedPosition.tokenId) return;

    try {
      this.isClaimingFees = true;
      let managerAddress: string | undefined = undefined;
      const contracts = this.web3Service.getContractsConfig();
      if (this.selectedPosition?.isExternal) {
        if (this.selectedPosition.managerAddress) {
          managerAddress = this.selectedPosition.managerAddress;
        } else if (this.selectedPosition.platformName === 'Uniswap') {
          managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        } else if (this.selectedPosition.platformName === 'PancakeSwap') {
          managerAddress = contracts.EXTERNAL_MANAGERS?.PancakeSwap || '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364';
        } else {
          managerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        }
      }
      
      const tx = await this.web3Service.claimFees(this.selectedPosition.tokenId, managerAddress);
      this.state.triggerNotification(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);
      await tx.wait();
      this.state.triggerNotification('Fees claimed successfully!');

      // Optimistic update
      const posToUpdate = this.yourPositions.list.find((p: any) => p.tokenId === this.selectedPosition.tokenId);
      if (posToUpdate) {
        posToUpdate.feeToken0 = 0;
        posToUpdate.feeToken1 = 0;
        posToUpdate.unclaimedFeesUsd = 0;
      }
      
      const newTotalUnclaimed = this.yourPositions.list.reduce((acc: number, p: any) => acc + (p.unclaimedFeesUsd || 0), 0);
      this.yourPositions.unclaimedFees = `$${formatSubscript(newTotalUnclaimed)}`;
      this.cdr.detectChanges();

      // Sync with backend
      const tokenId = this.selectedPosition.tokenId;
      try {
        await lastValueFrom(this.http.post(
          `${environment.apiUrl}/user/liquidity/user-positions/sync?chainId=${this.state.selectedNetworkId()}`,
          { tokenId, action: 'claim', networkId: this.state.selectedNetworkId() },
          { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } }
        ));

        await lastValueFrom(this.activityService.recordActivity({
          txHash: tx.hash,
          chainId: this.state.selectedNetworkId(),
          actionType: 'CLAIM_FEE',
          status: 'Success',
          payload: {
            poolAddress: this.selectedPosition.poolAddress,
            token0Symbol: this.selectedPosition.token0Symbol,
            token1Symbol: this.selectedPosition.token1Symbol,
            feeToken0: this.rawFeeAmount0,
            feeToken1: this.rawFeeAmount1
          },
          platformName: this.selectedPosition.isExternal ? 'External Aggregator' : 'DEX Native'
        }));
      } catch (err) {
        console.error('Backend sync failed', err);
      }

      this.closeClaimModal();
    } catch (err: any) {
      console.error(err);
      if (err?.code === 'ACTION_REJECTED' || err?.message?.includes('User denied') || err?.message?.includes('user rejected')) {
        this.state.triggerNotification('Transaction rejected by user');
      } else {
        this.state.triggerNotification(this.state.handleError(err, 'Transaction failed'));
      }
    } finally {
      this.isClaimingFees = false;
      this.cdr.markForCheck();
    }
  }
}
